import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableItem } from './SortableItem';
import { Eye, EyeOff, Plus, Trash2, Info, CheckCircle2, Smartphone } from 'lucide-react';
import type { ColumnConfig, ColumnType } from '../types';
import { DEFAULT_COLUMN_CONFIG } from '../utils/defaultColumnConfig';

interface Props {
  eventId: string;
  splitPoints: Array<{ id: string; name: string; distance: number }>;
  config: ColumnConfig[];
  onChange: (config: ColumnConfig[]) => Promise<void>;
  onColumnTypeSelect?: (type: ColumnType | null) => void;
}

const COLUMN_TYPES: { value: ColumnType; label: string; hint: string; required?: boolean }[] = [
  { value: 'rank', label: 'Rank', hint: 'Number (e.g., 1, 2, 3), "DNF", or leave empty' },
  { value: 'bib_number', label: 'Bib Number', hint: 'Text or number (e.g., "A123", "5001")', required: true },
  { value: 'name', label: 'Name', hint: 'Text (e.g., "John Smith")', required: true },
  { value: 'gender', label: 'Gender', hint: 'Must be exactly: "Male", "Female", "Non Binary", or leave empty' },
  { value: 'gender_position', label: 'Gender Position', hint: 'Number (e.g., 1, 2, 3) or leave empty' },
  { value: 'category', label: 'Category', hint: 'Text (e.g., "M35", "F40", "MSEN") or leave empty' },
  { value: 'category_position', label: 'Category Position', hint: 'Number (e.g., 1, 2, 3) or leave empty' },
  { value: 'club', label: 'Club', hint: 'Text (e.g., "Running Club") or leave empty' },
  { value: 'chip_time', label: 'Chip Time', hint: 'Format: "HH:MM:SS", "DNF", or leave empty' },
  { value: 'gun_time', label: 'Gun Time', hint: 'Format: "HH:MM:SS", "DNF", or leave empty' },
  { value: 'custom_1', label: 'Custom 1', hint: 'Any text or number (e.g., "Team A", "247234") or leave empty' },
  { value: 'custom_2', label: 'Custom 2', hint: 'Any text or number (e.g., "Sponsor Name", "12345") or leave empty' }
];

export default function ColumnEditor({ eventId, splitPoints, config, onChange, onColumnTypeSelect }: Props) {
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [showNewColumn, setShowNewColumn] = useState(false);
  const [newColumn, setNewColumn] = useState<Partial<ColumnConfig>>({
    type: 'name',
    csvHeader: '',
    displayName: '',
    visible: true,
    visibleOnMobile: false
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [selectedType, setSelectedType] = useState<ColumnType | null>(null);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (config.length > 0) {
      // Filter out split point columns that reference deleted split points
      const validSplitPointIds = new Set(splitPoints.map(point => point.id));
      const filteredConfig = config.filter(col => 
        col.type !== 'split_point' || (col.splitPointId && validSplitPointIds.has(col.splitPointId))
      );
      
      // Ensure all columns have the visibleOnMobile property
      const updatedConfig = filteredConfig.map(col => ({
        ...col,
        visibleOnMobile: col.visibleOnMobile === true ? true : 
          (col.visibleOnMobile === undefined ? ['rank', 'name', 'chip_time'].includes(col.type) : false)
      }));
      
      setColumns(updatedConfig);
    } else {

      const splitColumns: ColumnConfig[] = splitPoints.map(point => ({
        id: point.id,
        type: 'split_point',
        csvHeader: point.name.toLowerCase().replace(/\s+/g, '_'),
        displayName: point.name,
        visible: true,
        visibleOnMobile: false,
        splitPointId: point.id
      }));
      
      setColumns([...DEFAULT_COLUMN_CONFIG, ...splitColumns]);
    }
  }, [config, splitPoints]);

  useEffect(() => {
    if (showNewColumn) {
      const initialType = newColumn.type as ColumnType;
      setSelectedType(initialType);
      if (onColumnTypeSelect) {
        onColumnTypeSelect(initialType);
      }
    } else {
      setSelectedType(null);
      if (onColumnTypeSelect) {
        onColumnTypeSelect(null);
      }
    }
  }, [showNewColumn, newColumn.type, onColumnTypeSelect]);

  const saveChanges = async (newColumns: ColumnConfig[]) => {
    // Clear any existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      setSaveTimeout(null);
    }

    // Set a new timeout to save changes after a delay
    const timeout = setTimeout(async () => {
      setSaving(true);
      setSaveError(null);
      try {
        await onChange(newColumns);
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
      } catch (err: any) {
        setSaveError(err.message || 'Failed to save changes');
      } finally {
        setSaving(false);
      }
    }, 1000); // 1 second delay

    setSaveTimeout(timeout);
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columns.findIndex((i) => i.id === active.id);
      const newIndex = columns.findIndex((i) => i.id === over.id);
      const newColumns = arrayMove(columns, oldIndex, newIndex);
      setColumns(newColumns);
      await saveChanges(newColumns);
    }
  };

  const handleAddColumn = async () => {
    if (!newColumn.type || !newColumn.csvHeader || !newColumn.displayName) return;

    const id = `custom-${Date.now()}`;
    const newColumns = [
      {
        id,
        type: newColumn.type as ColumnType,
        csvHeader: newColumn.csvHeader,
        displayName: newColumn.displayName,
        visible: true,
        visibleOnMobile: ['rank', 'name', 'chip_time'].includes(newColumn.type as ColumnType)
      },
      ...columns
    ];
    
    setColumns(newColumns);
    await saveChanges(newColumns);

    setNewColumn({
      type: 'name',
      csvHeader: '',
      displayName: '',
      visible: true,
      visibleOnMobile: false
    });
    setShowNewColumn(false);
    setSelectedType(null);
    if (onColumnTypeSelect) onColumnTypeSelect(null);
  };

  const handleDeleteColumn = async (id: string) => {
    const column = columns.find(col => col.id === id);
    if (column?.type === 'split_point' || column?.type === 'custom') {
      // Allow deletion of custom columns
    } else {
      // Prevent deletion of standard columns that might be required
    }
    
    const newColumns = columns.filter(col => col.id !== id);
    setColumns(newColumns);
    await saveChanges(newColumns);
  };

  const handleToggleVisibility = async (id: string) => {
    const column = columns.find(col => col.id === id);
    if (column?.type === 'split_point') return;
    
    const newColumns = columns.map(col => 
      col.id === id ? { ...col, visible: !col.visible } : col
    );
    setColumns(newColumns);
    await saveChanges(newColumns);
  };

  const handleToggleMobileVisibility = async (id: string) => {
    const column = columns.find(col => col.id === id);
    if (column?.type === 'split_point') return;
    
    const newColumns = columns.map(col => 
      col.id === id ? { ...col, visibleOnMobile: col.visibleOnMobile === true ? false : true } : col
    );
    setColumns(newColumns);
    await saveChanges(newColumns);
  };

  const handleUpdateColumn = async (id: string, updates: Partial<ColumnConfig>) => {
    const column = columns.find(col => col.id === id);
    if (column?.type === 'split_point' && updates.displayName) return;
    
    const newColumns = columns.map(col => 
      col.id === id ? { ...col, ...updates } : col
    );
    setColumns(newColumns);
    await saveChanges(newColumns);
  };

  const handleTypeSelect = (type: ColumnType) => {
    setSelectedType(type);
    if (onColumnTypeSelect) {
      onColumnTypeSelect(type);
    }
    
    const columnType = COLUMN_TYPES.find(t => t.value === type);
    if (columnType) {
      setNewColumn(prev => ({
        ...prev,
        type,
        csvHeader: type,
        displayName: columnType.label
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-medium">Column Configuration</h3>
          {saving && (
            <div className="flex items-center gap-2 text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              <span className="text-sm">Saving...</span>
            </div>
          )}
          {showSaveSuccess && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm">Saved</span>
            </div>
          )}
          {saveError && (
            <div className="text-red-600 text-sm">
              {saveError}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowNewColumn(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800"
        >
          <Plus className="w-4 h-4" />
          Add Column
        </button>
      </div>

      {showNewColumn && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Column Type
              </label>
              <select
                value={newColumn.type}
                onChange={(e) => handleTypeSelect(e.target.value as ColumnType)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
              >
                {COLUMN_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CSV Header
              </label>
              <input
                type="text"
                value={newColumn.csvHeader}
                onChange={(e) => setNewColumn({ ...newColumn, csvHeader: e.target.value })}
                placeholder="e.g., runner_name"
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={newColumn.displayName}
                onChange={(e) => setNewColumn({ ...newColumn, displayName: e.target.value })}
                placeholder="e.g., Runner Name"
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
              />
            </div>
          </div>

          {selectedType && COLUMN_TYPES.find(t => t.value === selectedType)?.hint && (
            <div className="flex items-start gap-2 text-sm text-gray-600 bg-white p-3 rounded-md border border-gray-200">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium">Expected Format: </span>
                {COLUMN_TYPES.find(t => t.value === selectedType)?.hint}
                {COLUMN_TYPES.find(t => t.value === selectedType)?.required && (
                  <span className="text-red-600 ml-1">(Required)</span>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowNewColumn(false);
                setSelectedType(null);
                if (onColumnTypeSelect) onColumnTypeSelect(null);
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={handleAddColumn}
              disabled={!newColumn.type || !newColumn.csvHeader || !newColumn.displayName}
              className="px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
            >
              Add Column
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={columns.map(col => col.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="divide-y divide-gray-200">
              {columns.map((column) => (
                <SortableItem
                  key={column.id}
                  id={column.id}
                  className="p-4 bg-white hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Column Type
                        </label>
                        <select
                          value={column.type}
                          onChange={(e) => {
                            handleUpdateColumn(column.id, { type: e.target.value as ColumnType });
                            if (onColumnTypeSelect) onColumnTypeSelect(e.target.value as ColumnType);
                          }}
                          onFocus={() => {
                            if (onColumnTypeSelect) onColumnTypeSelect(column.type);
                          }}
                          disabled={column.type === 'split_point'}
                          className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500 disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          {COLUMN_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                          {column.type === 'split_point' && (
                            <option value="split_point">Split Point</option>
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          CSV Header
                        </label>
                        <input
                          type="text"
                          value={column.csvHeader}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setColumns(prev => 
                              prev.map(col => 
                                col.id === column.id ? { ...col, csvHeader: newValue } : col
                              )
                            );
                          }}
                          onBlur={(e) => handleUpdateColumn(column.id, { csvHeader: e.target.value })}
                          className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Display Name
                        </label>
                        {column.type === 'split_point' ? (
                          <div className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-gray-500">
                            {column.displayName}
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={column.displayName}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setColumns(prev => 
                                prev.map(col => 
                                  col.id === column.id ? { ...col, displayName: newValue } : col
                                )
                              );
                            }}
                            onBlur={(e) => handleUpdateColumn(column.id, { displayName: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                          />
                        )}
                      </div>
                    </div>
                    {column.type !== 'split_point' && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleVisibility(column.id)}
                          className="p-1.5 text-gray-500 hover:text-gray-700"
                          title={column.visible ? 'Hide column' : 'Show column'}
                        >
                          {column.visible ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleToggleMobileVisibility(column.id)}
                         className={`p-1.5 ${column.visibleOnMobile ? 'text-blue-500 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                          title={column.visibleOnMobile ? 'Hide on mobile' : 'Show on mobile'}
                        >
                         {column.visibleOnMobile ? (
                           <Smartphone className="w-4 h-4" />
                         ) : (
                           <EyeOff className="w-4 h-4" />
                         )}
                        </button>
                        <button
                          onClick={() => handleDeleteColumn(column.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600"
                          title="Delete column"
                          disabled={column.type === 'split_point'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {(column.type !== 'split_point') && (
                    <div></div>
                  )}
                </SortableItem>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}